import { ref, computed } from "vue";
import { db, auth } from "@/firebase";
import { collection, onSnapshot, query, where, doc, getDoc, getDocs } from "firebase/firestore";
import { logDebug, logError, logInfo, logWarn } from "@/utils/secureLogger";
import { checkUsersExist, sanitizeUserDisplay, validateSessionUsers } from "@/utils/sessionCleanup";

// Global reactive state - shared across components
const upcomingSessions = ref<any[]>([]);
const completedSessions = ref<any[]>([]);
const cancelledSessions = ref<any[]>([]);
const isLoadingSessions = ref(true);
const tutorCache = ref<{ [key: string]: any }>({});
const userAverageRating = ref<number>(0); // ✅ Pre-calculated rating from user document
let unsubscribe: (() => void) | null = null;

// Get current authenticated user ID
const getCurrentUserId = () => {
  return auth.currentUser?.uid || null;
};

// Fetch actual ratings from feedbacks collection for a session
const fetchRatingsForSession = async (sessionId: string, currentUserId: string) => {
  try {
    const feedbacksQuery = query(collection(db, "feedbacks"), where("sessionID", "==", sessionId));

    const feedbackSnapshot = await getDocs(feedbacksQuery);
    const feedbacks = feedbackSnapshot.docs.map((doc) => doc.data());

    // Get rating given by current user (student) to tutor
    const userFeedback = feedbacks.find((feedback) => feedback.fromUserID === currentUserId);
    const yourRating = userFeedback?.overallScore?.toString() || "0";

    // Get rating given by tutor to current user (student)
    const tutorFeedback = feedbacks.find((feedback) => feedback.toUserID === currentUserId);
    const tutorRating = tutorFeedback?.overallScore?.toString() || "0";

    return {
      yourRating,
      tutorRating,
    };
  } catch (error) {
    logError(
      "Error fetching ratings for session",
      error instanceof Error ? error : new Error(String(error))
    );
    return {
      yourRating: "0",
      tutorRating: "0",
    };
  }
};

// Fetch tutor data by tutorId
const fetchTutorData = async (tutorId: string) => {
  if (tutorCache.value[tutorId]) {
    return tutorCache.value[tutorId];
  }

  try {
    const tutorDocRef = doc(db, "users", tutorId);
    const tutorDoc = await getDoc(tutorDocRef);

    if (tutorDoc.exists()) {
      const tutorData = tutorDoc.data();
      tutorCache.value[tutorId] = tutorData;
      return tutorData;
    } else {
      logDebug("No tutor found with specified ID");
      return null;
    }
  } catch (error) {
    logError(
      "Error fetching tutor data",
      error instanceof Error ? error : new Error(String(error))
    );
    return null;
  }
};

// Batch fetch tutor data for multiple tutor IDs
const batchFetchTutorData = async (tutorIds: string[]) => {
  const uniqueTutorIds = [...new Set(tutorIds)];
  const uncachedIds = uniqueTutorIds.filter((id) => !tutorCache.value[id]);

  if (uncachedIds.length === 0) {
    logDebug("All tutor data already cached, skipping fetch");
    return tutorCache.value;
  }

  logInfo("Batch fetching tutor profiles", {
    tutorIds: uncachedIds,
    count: uncachedIds.length,
  });

  // Fetch all tutor data in parallel
  const tutorPromises = uncachedIds.map(async (tutorId) => {
    try {
      const tutorDocRef = doc(db, "users", tutorId);
      const tutorDoc = await getDoc(tutorDocRef);

      if (tutorDoc.exists()) {
        const tutorData = tutorDoc.data();
        tutorCache.value[tutorId] = tutorData;
        logDebug("Tutor data fetched successfully", {
          tutorId,
          displayName: tutorData?.displayName,
          firstName: tutorData?.firstName,
          lastName: tutorData?.lastName,
          accountStatus: tutorData?.accountStatus,
        });

        // Log if tutor is archived
        if (tutorData?.accountStatus === "archived") {
          logWarn("Archived tutor detected", {
            tutorId,
            tutorName: tutorData?.displayName || tutorData?.firstName || "Unknown",
          });
        }

        return { tutorId, tutorData };
      } else {
        logError(
          "No tutor found with specified ID in users collection",
          new Error(`Tutor document not found: ${tutorId}`)
        );
        tutorCache.value[tutorId] = null;
        return { tutorId, tutorData: null };
      }
    } catch (error) {
      logError(
        "Error fetching tutor data",
        error instanceof Error ? error : new Error(String(error))
      );
      tutorCache.value[tutorId] = null;
      return { tutorId, tutorData: null };
    }
  });

  await Promise.all(tutorPromises);
  return tutorCache.value;
};

// Transform Firebase session data to component expected format
const transformSessionData = async (session: any) => {
  const startDate = session.startAt.toDate();
  const endDate = session.endAt.toDate();

  // Use cached tutor data (should already be fetched)
  const tutorData = tutorCache.value[session.tutorId];
  // Check if tutor exists and validate session if needed
  const tutorExists = !!tutorData;
  if (!tutorExists && ["pending", "confirmed", "in-progress"].includes(session.sessionStatus)) {
    // Auto-cancel session if tutor doesn't exist
    await validateSessionUsers(session.id, session.tutorId, session.studentId);
  }
  // Robust tutor name logic
  const tutorName = tutorData
    ? tutorData.displayName ||
      `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim() ||
      tutorData.email ||
      `Tutor ${session.tutorId}`
    : "Deactivated User";

  // Determine feedback status for current user
  const currentUserId = getCurrentUserId();
  let feedbackStatus: "given" | "pending" = "pending";
  if (currentUserId && session[`feedbackStatus_${currentUserId}`] === "given") {
    feedbackStatus = "given";
  }

  // Fetch actual ratings from feedbacks collection for completed sessions
  let ratings = { yourRating: "0", tutorRating: "0" };
  let feedbacks: any[] = [];
  if (currentUserId && session.sessionStatus === "completed") {
    ratings = await fetchRatingsForSession(session.id, currentUserId);

    // Fetch all feedbacks for this session from top-level feedbacks collection
    try {
      const feedbacksQuery = query(
        collection(db, "feedbacks"),
        where("sessionID", "==", session.id)
      );
      const feedbacksSnapshot = await getDocs(feedbacksQuery);
      feedbacks = feedbacksSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      logDebug("Error fetching feedbacks for session", { sessionId: session.id });
    }
  }

  return {
    ...session,
    date: startDate.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    time: `${startDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })} - ${endDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`,
    tutorName: tutorName,
    tutorId: session.tutorId, // Preserve tutorId for availability loading
    tutorProfileImage: tutorData?.profileImage || null, // Include tutor profile photo
    status: session.sessionStatus,
    type: (session.mode?.toLowerCase() === "face-to-face" ? "face-to-face" : "online") as
      | "face-to-face"
      | "online",
    meetingLink: session.meetingLink, // Explicitly include meeting link
    feedbackStatus: feedbackStatus,
    yourRating: ratings.yourRating,
    tutorRating: ratings.tutorRating,
    feedback: session.feedback || "",
    feedbacks: feedbacks, // Include all feedbacks for the session
    // Include materials with proper date conversion
    materials:
      session.materials?.map((material: any) => ({
        ...material,
        uploadedAt: material.uploadedAt?.toDate
          ? material.uploadedAt.toDate()
          : material.uploadedAt,
      })) || [],
    // Include actual Date objects for time calculations
    startAt: startDate,
    endAt: endDate,
  };
};

const initializeSessionListener = () => {
  if (unsubscribe) return; // Already initialized

  const currentUserId = getCurrentUserId();
  if (!currentUserId) {
    // Don't show warning, just return silently - user might not be logged in yet
    return;
  }

  // ✅ Fetch user's pre-calculated rating from user document
  const fetchUserRating = async () => {
    try {
      const userDoc = await getDoc(doc(db, "users", currentUserId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userAverageRating.value = userData?.averageRating || 0;
        logDebug("Loaded pre-calculated user rating", { rating: userAverageRating.value });
      }
    } catch (error) {
      logError(
        "Error fetching user rating",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };

  // Fetch rating immediately
  fetchUserRating();

  const sessionsRef = collection(db, "sessions");
  const sessionsQuery = query(
    sessionsRef,
    where("studentId", "==", currentUserId),
    where("status", "==", "active")
  );

  unsubscribe = onSnapshot(sessionsQuery, async (snapshot) => {
    logDebug("Sessions store snapshot received");

    try {
      const allSessions = snapshot.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        };
      });

      // Extract all unique tutor IDs for batch fetching
      const allTutorIds = allSessions.map((session: any) => session.tutorId).filter(Boolean);

      // Batch fetch all tutor data first
      await batchFetchTutorData(allTutorIds);

      // Transform and filter upcoming sessions (pending, confirmed, or in-progress)
      // ✅ Filter out sessions with archived tutors for student view
      const upcomingFiltered = allSessions.filter((session: any) => {
        const isUpcomingStatus = ["pending", "confirmed", "in-progress"].includes(
          session.sessionStatus
        );
        if (!isUpcomingStatus) return false;

        // Check if tutor is archived
        const tutorData = tutorCache.value[session.tutorId];
        const isTutorArchived = tutorData?.accountStatus === "archived";

        if (isTutorArchived) {
          logInfo("Hiding upcoming session with archived tutor", {
            sessionId: session.id,
            tutorId: session.tutorId,
            tutorName: tutorData?.displayName || tutorData?.firstName || "Unknown",
          });
          return false;
        }

        return true;
      });

      const upcomingTransformed = await Promise.all(upcomingFiltered.map(transformSessionData));

      upcomingSessions.value = upcomingTransformed.sort(
        (a: any, b: any) => a.startAt.getTime() - b.startAt.getTime()
      );

      // Transform and filter completed sessions
      const completedFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "completed"
      );

      const completedTransformed = await Promise.all(completedFiltered.map(transformSessionData));

      completedSessions.value = completedTransformed.sort(
        (a: any, b: any) => b.startAt.getTime() - a.startAt.getTime()
      );

      // Transform and filter cancelled/declined sessions
      const cancelledFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "cancelled" || session.sessionStatus === "declined"
      );

      const cancelledTransformed = await Promise.all(cancelledFiltered.map(transformSessionData));

      cancelledSessions.value = cancelledTransformed.sort(
        (a: any, b: any) => b.startAt.getTime() - a.startAt.getTime()
      );

      logInfo("Sessions store updated successfully");

      // Set loading to false after data is processed
      isLoadingSessions.value = false;
    } catch (error) {
      logError(
        "Error processing sessions",
        error instanceof Error ? error : new Error(String(error))
      );
      isLoadingSessions.value = false;
    }
  });
};

export function useSessionStore() {
  // Initialize on first use
  if (!unsubscribe) {
    initializeSessionListener();

    // If initialization failed due to no auth, set up a retry mechanism
    if (!unsubscribe) {
      const checkAuthAndRetry = () => {
        const currentUserId = getCurrentUserId();
        if (currentUserId && !unsubscribe) {
          initializeSessionListener();
        }
      };

      // Check every 500ms for authentication
      const retryInterval = setInterval(() => {
        if (unsubscribe) {
          clearInterval(retryInterval);
        } else {
          checkAuthAndRetry();
        }
      }, 500);

      // Stop retrying after 10 seconds
      setTimeout(() => clearInterval(retryInterval), 10000);
    }
  }

  // Computed properties for summary cards
  const totalCompletedSessions = computed(() => completedSessions.value.length);

  const totalUpcomingSessions = computed(() => upcomingSessions.value.length);

  const totalCancelledSessions = computed(() => cancelledSessions.value.length);

  const averageRating = computed(() => {
    // ✅ Return pre-calculated rating from user document (INSTANT!)
    return userAverageRating.value.toFixed(1);
  });

  const totalHours = computed(() => {
    if (completedSessions.value.length === 0) return "0";

    const totalMinutes = completedSessions.value.reduce((sum: number, session: any) => {
      // Calculate actual session duration in minutes
      const startTime = session.startAt;
      const endTime = session.endAt;

      // Calculate the difference in milliseconds and convert to minutes
      const durationMs = endTime - startTime;
      const durationMinutes = Math.round(durationMs / (1000 * 60));

      // Only count positive durations
      return durationMinutes > 0 ? sum + durationMinutes : sum;
    }, 0);

    // Never show negative hours
    return Math.max(0, Math.round(totalMinutes / 60)).toString();
  });

  return {
    upcomingSessions,
    completedSessions,
    cancelledSessions,
    isLoadingSessions,
    totalCompletedSessions,
    totalUpcomingSessions,
    totalCancelledSessions,
    averageRating,
    totalHours,
  };
}
